import { Button, DatePicker, Drawer, Form, Input, Select, message } from "antd";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { LeadInformationType } from "./types";
import { leadTypeOptions } from "./options";
import requiredRule from "../../../utils/formRules/requiredRule";
import { useEffect, useState } from "react";
import { apiCall } from "../../../utils/apiCall";
import dayjs from "dayjs";

interface LeadInfoDrawerProps {
  open: boolean;
  setOpenedDrawer: any;
  info: LeadInformationType | undefined;
  refetch: any;
  leadNo: string | undefined;
  userName: string;
}

interface MediaIdOptionsType {
  localMarketingId?: number;
  categoryId?: number;
  code: string;
  branchCode: string;
  name: string;
}

const LeadInfoDrawer = ({
  open,
  setOpenedDrawer,
  info,
  refetch,
  leadNo,
  userName,
}: LeadInfoDrawerProps) => {
  const [form] = Form.useForm();
  const [editMedia, setEditMedia] = useState<boolean>(false);
  const [mediaId, setMediaId] = useState();
  const [mediaGroup, setMediaGroup] = useState<"c" | "l">();
  const [mediaIdData, setMediaIdData] = useState<any>([]);
  const [mediaLeadType, setMediaLeadType] = useState<string | undefined>();
  const [mediaIdOptions, setMediaIdOptions] = useState([]);
  const [isFetchMediaId, setIsFetchMediaId] = useState(false);

  const filterOption: any = (
    input: string,
    option: { label: string; value: string }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const dlLeadTypeOptions = (leadTypeOptions.find(o => o.value === "DL")?.children || [])
    .filter(c => ["LD", "GD", "BD"].includes(c.value))
    .map(c => ({ value: `DL${c.value}`, label: `DL-${c.title}` }));

  const fetchMediaId = async (leadNo: string) => {
    if (isFetchMediaId) return;
    setIsFetchMediaId(true);
    try {
      const header = {
        headers: {
          "x-api-key": process.env.REACT_APP_X_API_KEY,
        },
      };
      const data = await apiCall({
        method: "GET",
        endPoint:
          process.env.REACT_APP_SALES_ADMIN +
          "/leads/admin/" +
          leadNo +
          "/mediaId",
        header: header,
      });

      if (data) {
        //console.log("id", data.categoryId || data.localMarketingId);
        setMediaId(data.categoryId || data.localMarketingId);
        setMediaGroup(data.categoryId ? "c" : "l");
      } else {
        setMediaId(undefined);
        setMediaGroup(undefined);
      }
      setEditMedia(false);
    } finally {
      setTimeout(() => setIsFetchMediaId(false), 1000);
    }
  };

  const fetchMediaIdOptions = async () => {
    const header = {
      headers: {
        "x-api-key": process.env.REACT_APP_X_API_KEY,
      },
    };
    const data = await apiCall({
      method: "GET",
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/leads/admin/options/media",
      header: header,
    });
    if (data) {
      setMediaIdData(data);
    }
  };

  const fetchMediaOptions = async (value: string) => {
    if (!value) {
      setMediaIdOptions([]);
      setMediaLeadType(undefined);
      form.setFieldsValue({ type: undefined, mediaCode: undefined });
      return;
    }
    if (!mediaIdData) return;
    let pool: any;

    if (value === "CL") pool = mediaIdData?.CL;
    else if (value === "DLBD") pool = mediaIdData?.DLBD;
    else if (value === "DLGD") pool = mediaIdData?.DLGD;
    else if (value === "DLLD") pool = mediaIdData?.DLLD;

    setMediaIdOptions(
      pool.map((item: MediaIdOptionsType) => {
        return {
          value: item.categoryId
            ? `c-${item.categoryId}`
            : `l-${item.localMarketingId}`,
          label: item.code ? `${item.code}(${item.name})` : `${item.name}/${item.branchCode}`,
        };
      })
    );
    setMediaLeadType(value);
    form.setFieldsValue({ type: value });
  }

  const handleSubmit = async () => {
    const { mediaCode, expiry } = form.getFieldsValue();
    if (mediaCode && expiry) {
      const result = await apiCall({
        method: "PATCH",
        endPoint:
          process.env.REACT_APP_SALES_ADMIN +
          "/leads/admin/" +
          leadNo +
          "/information",
        header: {
          headers: {
            "x-api-key": process.env.REACT_APP_X_API_KEY,
          },
        },
        payload: {
          expiryDate: dayjs.utc(expiry).format("YYYY-MM-DD"),
          editedBy: userName,
          ...(mediaCode.startsWith("l-")
            ? { localMarketingId: parseFloat(mediaCode.replace("l-", "")) }
            : { categoryId: parseFloat(mediaCode.replace("c-", "")) }),
        },
      });
      if (result?.status === "SUCCESS") {
        setOpenedDrawer("");
        message.success(
          renderAntDMessageConfig("리드정보 변경 사항이 저장되었습니다.")
        );
        form.resetFields();
        refetch(leadNo);
      } else {
        message.error(
          renderAntDMessageConfig("리드정보 변경 사항 저장에 실패했습니다.")
        );
      }
    }
  };

  useEffect(() => {
    if (info && open) {
      if (leadNo) {
        fetchMediaId(leadNo);
      }
      form.setFieldsValue({
        leadNo: leadNo,
        assignee: `${info.name}(${info.companyNumber})`,
        cycle: info.term,
        distribution: info.distributionDate
          ? dayjs.utc(info.distributionDate).format("YYYY-MM-DD")
          : "",
        create: info.createDate
          ? dayjs.utc(info.createDate).format("YYYY-MM-DD")
          : "",
        expiry: info.expiryDate ? dayjs.utc(info.expiryDate) : undefined,
      });
    }
  }, [info, open, leadNo, form]);

  useEffect(() => {
    if (info && open && mediaIdData) {
      let dlLeadType = "";
      if (info.cldl === "CL") {
        dlLeadType = "CL";
      } else {
        if (info.leadType === "PD") {
          if (mediaGroup === "c") {
            if (mediaIdData?.DLBD?.some((item: any) => item?.categoryId === mediaId)) dlLeadType = "DLBD";
            if (mediaIdData?.DLLD?.some((item: any) => item?.categoryId === mediaId)) dlLeadType = "DLLD";
          } else if (mediaGroup === "l") {
            dlLeadType = "DLGD";
          }
        } else dlLeadType = "DL" + info.leadType;
      }

      setMediaLeadType(dlLeadType);
      form.setFieldsValue({
        type: dlLeadType,
        mediaCode: mediaId ? `${mediaGroup}-${mediaId}` : null,
      });
      fetchMediaOptions(dlLeadType);
    }
  }, [mediaIdData, info, open, mediaId, mediaGroup]);

  useEffect(() => {
    if (leadNo) {
      fetchMediaId(leadNo);
      fetchMediaIdOptions();
    }
  }, [leadNo]);
  return (
    <Drawer
      open={open}
      onClose={() => {
        setOpenedDrawer("");
        form.resetFields();
      }}
      id="leadInfoDrawerWrapper"
      title="리드정보 수정"
      width={500}
      footer={
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setOpenedDrawer("");
              form.resetFields();
            }}
          >
            취소
          </Button>
          <Button
            disabled={(!mediaId && !editMedia)}
            className="ml-2"
            type="primary"
            onClick={handleSubmit}
          >
            저장
          </Button>
        </div>
      }
    >
      <Form form={form} name="lead-info" layout="vertical">
        <Form.Item label="리드번호" name="leadNo">
          <Input disabled />
        </Form.Item>
        <Form.Item label="현재 담당자 이름" name="assignee">
          <Input disabled />
        </Form.Item>
        <Form.Item label="리드주기" name="cycle">
          <Input disabled />
        </Form.Item>
        {info?.cldl !== 'CL' && (
          <Form.Item label="미디어코드 리드유형 (리드타입)" name="type" >
            <Select
              value={mediaLeadType}
              options={dlLeadTypeOptions}
              onChange={(value) => {
                fetchMediaOptions(value);
                form.setFieldsValue({ mediaCode: undefined });
              }}
            />
          </Form.Item>
        )}
        <Form.Item
          label="미디어 (리드유입)"
          name="mediaCode"
          rules={[requiredRule]}
        >
          <Select
            options={mediaIdOptions}
            onChange={(value) => setEditMedia(!!value)}
            filterOption={filterOption}
            showSearch />
        </Form.Item>
        {!mediaId && (
          <p className="warning-text">
            저장하기 전에 올바른 미디어(리드유입) 정보를 선택하여 주세요.
          </p>
        )}
        <Form.Item label="배분일" name="distribution">
          <Input disabled />
        </Form.Item>
        <Form.Item label="마감일" name="expiry" rules={[requiredRule]}>
          <DatePicker className="w-full" />
        </Form.Item>
        <p style={{ color: "gray", marginTop: -15, marginBottom: 15 }}>
          마감일 변경은 F:첫째주 월요일/R:두째주 월요일/O:세쨰주 월요일/S:네쨰주
          월요일을 선택하여 주세요
        </p>
        <Form.Item label="최초생성일" name="create">
          <Input disabled />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default LeadInfoDrawer;
